import { useEffect, useMemo, useRef, useState } from "react";
import { permissionData } from "../../../assets/data";
import Helpers from "../../../commons/helpers";
import { ICodename, IRole, RoleResourcePermission } from "../../../commons/interfaces";
import { RoleHelpers } from "../../../commons/roleHelpers";
import { Mode, Permission, RoleType } from "../../../constants/enums";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import ResourceService from "../../../services/identity/resourceService";
import RoleService from "../../../services/identity/role.serice";
import { IResource } from "./../../../commons/interfaces";

export type IRoleError = { [k in keyof IRole]?: string };

export const useData = ({
    idDetail,
    getAllResource,
    getAllService,
    setResources,
    onChangeMode,
}: {
    idDetail?: string;
    getAllResource: (serviceCode: string) => Promise<void>;
    getAllService: (searchText?: string) => Promise<void>;
    setResources: React.Dispatch<React.SetStateAction<IResource[]>>;
    onChangeMode: (mode: Mode) => void;
}) => {
    const { clientId, userInfo, serviceCode, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();

    const { roleSet, roleDetailType } = useMemo(() => RoleHelpers.getRole(userInfo?.userProfile?.roleType), [userInfo?.userProfile?.roleType]);
    const acUpdate = useMemo(() => !Helpers.isNullOrEmpty(idDetail), [idDetail]);

    const permissionList = useMemo<ICodename[]>(() => permissionData, []);

    const permissionValues = useMemo<Permission[]>(
        () =>
            permissionList.reduce((acc, cur) => {
                acc.push(cur.code);
                return acc;
            }, []),
        [permissionList]
    );

    const fullPermission = useMemo<number>(() => permissionValues.reduce((acc, cur) => (acc = acc | cur), 0), [permissionValues]);

    const ref = useRef<IRole>();

    const [data, setData] = useState<IRole>({
        serviceCode,
        organizationId: userInfo?.userProfile?.organizationId || "0",
        roleResourcePermissions: [],
        type: roleSet | RoleType.Custom,
    } as IRole);

    const [error, setError] = useState<IRoleError>({} as IRoleError);

    const getResourceTemplate = async ({
        type,
        serviceCode,
        roleResourcePermissions,
    }: {
        type: number;
        serviceCode: string;
        roleResourcePermissions: RoleResourcePermission[];
    }) => {
        try {
            if (!RoleHelpers.isOrganizationUser(userInfo?.userProfile)) return;

            const resourceTemplate = await ResourceService.getTemplate({
                type: RoleHelpers.isCustom(type) ? userInfo?.userProfile?.roleType : type,
                serviceCode,
            });
            const result: RoleResourcePermission[] = [];

            for (const resource of resourceTemplate) {
                const firstResourcePermissions = resource.resourcePermissions?.[0];
                const curResource = roleResourcePermissions.find((el) => el.resourceCode === resource.resourceCode);
                result.push({
                    policyId: undefined,
                    resourceCode: resource.resourceCode,
                    permission: !Helpers.isNullOrEmpty(curResource?.permission) ? curResource?.permission : firstResourcePermissions.permission,
                    quota: 0,
                    id: undefined,
                    status: undefined,
                    createTime: undefined,
                    createUser: undefined,
                    updateTime: undefined,
                    updateUser: undefined,
                    enablePermission: firstResourcePermissions.permission,
                } as RoleResourcePermission);
            }
            setData((prev) => ({
                ...prev,
                roleResourcePermissions: result,
            }));
            setResources(
                (resourceTemplate || []).map((el) => ({
                    moduleResourceResponses: [],
                    resourceDetails: el.resourceDetails,
                    resourceType: el.resourceType,
                    tenantCode: el.tenantCode,
                    serviceCode: el.serviceCode,
                    organizationId: el.organizationId,
                    clientId: el.clientId,
                    resourceCode: el.resourceCode,
                    resourceURI: el.resourceURI,
                    resourceName: el.resourceName,
                    description: el.description,
                    parentResource: el.parentResource,
                    extraInformation: el.extraInformation,
                    accessMode: el.accessMode,
                    adminFeature: el.adminFeature,
                    gene: el.gene,
                    searchText: el.searchText,
                    id: el.id,
                    status: el.status,
                    createTime: el.createTime,
                    createUser: el.createUser,
                    updateTime: el.updateTime,
                    updateUser: el.updateUser,
                }))
            );
            return result;
        } catch (error) {
            setData((prev) => ({
                ...prev,
                roleResourcePermissions: [],
            }));
            setResources([]);

            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    useEffect(() => {
        const getDetail = async (id: string) => {
            const result = await RoleService.getDetail(id);

            if (!RoleHelpers.isOrganizationUser(userInfo?.userProfile)) await getAllResource(result?.serviceCode);

            if (RoleHelpers.isOrganizationUser(userInfo?.userProfile)) {
                const roleResourcePermissions = await getResourceTemplate({
                    type: result.type,
                    serviceCode: result?.serviceCode,
                    roleResourcePermissions: result?.roleResourcePermissions || [],
                });
                result.roleResourcePermissions = roleResourcePermissions || [];
            }
            if (result.type === userInfo?.userProfile?.roleType) onChangeMode(Mode.View);
            setData({ ...result });
            ref.current = { ...result };
        };
        const getData = async () => {
            try {
                onShowLoading();
                await getAllService();
                if (Helpers.isNullOrEmpty(idDetail)) {
                    if (!RoleHelpers.isOrganizationUser(userInfo?.userProfile)) await getAllResource(serviceCode);
                    if (RoleHelpers.isOrganizationUser(userInfo?.userProfile))
                        await getResourceTemplate({
                            type: data.type,
                            serviceCode: data?.serviceCode,
                            roleResourcePermissions: [],
                        });
                }

                if (!Helpers.isNullOrEmpty(idDetail)) await getDetail(idDetail);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        };
        getData();
    }, [userInfo?.userProfile?.organizationId, idDetail]);

    const onChangeValue = (key: keyof IRole) => async (value: string | number | RoleResourcePermission[]) => {
        try {
            if (key === "serviceCode") {
                onShowLoading();
                setData((prev) => {
                    const temp = { ...prev };
                    Object.assign(temp, { [key]: value });
                    temp.roleResourcePermissions = [];
                    return temp;
                });
                await getAllResource(value as string);
                onHideLoading();
            }

            if (key !== "serviceCode") {
                setData((prev) => {
                    const temp = { ...prev };
                    Object.assign(temp, { [key]: value });
                    return temp;
                });
            }

            if (key === "type" && RoleHelpers.isOrganizationUser(userInfo?.userProfile)) {
                onShowLoading();
                await getResourceTemplate({
                    type: value as number,
                    serviceCode: data?.serviceCode,
                    roleResourcePermissions: [],
                });
                onHideLoading();
            }

            if (!Helpers.isNullOrEmpty(error[key])) setError((prev) => Object.assign(prev, { [key]: undefined }));
        } catch (error) {
            console.log({ error });
        }
    };

    const onAddResource = (resourceData: IResource[]) => {
        const result: RoleResourcePermission[] = [];
        const temp = data?.roleResourcePermissions || [];
        const resourceIds = [...temp].map((el) => el.resourceCode);
        for (const resource of resourceData) {
            if (resourceIds.includes(resource.resourceCode)) {
                result.push(temp.find((el) => el.resourceCode === resource.resourceCode));
                continue;
            }
            result.push({
                policyId: undefined,
                resourceCode: resource.resourceCode,
                permission: fullPermission,
                quota: 0,
                id: undefined,
                status: undefined,
                createTime: undefined,
                createUser: undefined,
                updateTime: undefined,
                updateUser: undefined,
            } as RoleResourcePermission);
        }
        onChangeValue("roleResourcePermissions")(result);
    };

    const isEdited = useMemo(() => JSON.stringify(ref.current) !== JSON.stringify(data), [data, ref.current]);

    return {
        data,
        error,
        isEdited,
        onChangeValue,

        acUpdate,

        setError,
        onAddResource,
    };
};

export default useData;
